import Link from "next/link";
import { Hyphenated } from "@/utils/Hyphenated";
import NumberCard from "@/components/Cards/NumberCard";

interface PhoneNumber {
  id: string;
  number: string;
  status: string;
  countries: {
    country_flag: string;
    country_name: string;
    country_tel_code: string;
  };
  week_price?: number;
}

interface NumbersSectionProps {
  title: string;
  numbers: PhoneNumber[];
  countryName: string;
  viewAllUrl: string;
  isPrivate?: boolean;
  serviceSpecific?: boolean;
  countryParam?: string;
}

/**
 * NumbersSection component for displaying phone numbers
 * Can be used for both free and private numbers
 */
export default function NumbersSection({ 
  title, 
  numbers, 
  countryName, 
  viewAllUrl, 
  isPrivate = false,
  serviceSpecific = false,
  countryParam
}: NumbersSectionProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {title} ({numbers.length})
        </h2>
        <Link 
          href={viewAllUrl}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          View All {isPrivate ? "Private" : "Free"} Numbers
        </Link>
      </div>
      
      {numbers.length === 0 ? (
        <div className="bg-gray-50 p-8 rounded-lg text-center">
          <p className="text-gray-600">
            We currently have no available {isPrivate ? "private" : "free"} numbers for {countryName}.
            {isPrivate 
              ? " Check back soon for new additions." 
              : " We update our numbers daily so please try again soon."
            }
          </p>
        </div>
      ) : (
        <div className="gap-4 w-full h-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {numbers.map((number) => (
            <NumberCard
              key={number.id}
              country={`${number.countries.country_flag} ${number.countries?.country_name}`}
              status={number.status.toLowerCase()}
              number={`+${number.countries.country_tel_code}-${number.number}`}
              href={isPrivate 
                ? `/account/rent-number?pn=${number.id}`
                : serviceSpecific && countryParam
                  ? `/free-temporary-numbers/${countryParam}/${number.countries.country_tel_code}${number.number}`
                  : `/free-temporary-numbers/${Hyphenated(countryName)}/${number.countries.country_tel_code}${number.number}`
              }
              price={isPrivate ? number.week_price : undefined}
              isPrivate={isPrivate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
